import { PropsWithChildren, Suspense, memo, useEffect, useState } from "react";
import { StylesProvider, createGenerateClassName } from "@material-ui/core";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  AlertProvider,
  CalendarPickerProvider,
  CollegeHunksTheme,
  DialogContextController,
  Loader,
  SplitContextController,
} from "@chhjit/react-components";

import { ErrorBoundary } from "../../features/error-boundary";
import {
  GrowthBookProvider,
  useGrowthBookAttributes,
} from "../../shared/libs/growthbook";
import { SPLIT_AUTH_KEY } from "../../shared/configs";

/**
 * Null-rendering component mounted inside GrowthBookProvider.
 * Keeps GrowthBook attributes in sync with booking store data so that
 * experiments can target and re-evaluate based on user input mid-flow.
 */
const GrowthBookAttributesSetter = memo(() => {
  useGrowthBookAttributes();
  return null;
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const generateClassName = createGenerateClassName({
  productionPrefix: "chhj-booking-app",
});

export const AppProviders = memo(({ children }: PropsWithChildren) => {
  const [fingerprint, setFingerprint] = useState<string>();
  const [fontsLoaded, setFontsLoaded] = useState(true);

  // Detect when fonts are loaded
  useEffect(() => {
    if (typeof window === "undefined" || !document.fonts) {
      // Fallback for SSR or browsers without Font Loading API
      setFontsLoaded(true);
      return;
    }

    // Timeout fallback - show content after 2 seconds max
    const timeout = setTimeout(() => {
      setFontsLoaded(true);
    }, 2000);

    // Wait for fonts to load
    document.fonts.ready
      .then(() => {
        clearTimeout(timeout);
        setFontsLoaded(true);
      })
      .catch(() => {
        // Even if fonts fail to load, show content
        clearTimeout(timeout);
        setFontsLoaded(true);
      });

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    // Lazy load FingerprintJS to reduce initial bundle size
    // It's only needed for Split.io, which can wait until after initial render
    const loadFingerprint = async () => {
      // Use dynamic import to code-split FingerprintJS
      const FingerprintJS = (await import("@fingerprintjs/fingerprintjs"))
        .default;
      const fp = await FingerprintJS.load();
      const result = await fp.get();
      setFingerprint(result.visitorId); // Unique fingerprint ID
    };

    // Defer loading until after initial render
    if (typeof window !== "undefined") {
      if ("requestIdleCallback" in window) {
        requestIdleCallback(() => {
          loadFingerprint().catch(() => {
            // Silently fail - fingerprint is not critical
          });
        });
      } else {
        setTimeout(() => {
          loadFingerprint().catch(() => {
            // Silently fail - fingerprint is not critical
          });
        }, 100);
      }
    }
  }, []);

  // Show loader while fonts are loading
  if (!fontsLoaded) {
    return (
      <StylesProvider generateClassName={generateClassName}>
        <CollegeHunksTheme>
          <Loader size="large" />
        </CollegeHunksTheme>
      </StylesProvider>
    );
  }

  return (
    <StylesProvider generateClassName={generateClassName}>
      <CollegeHunksTheme>
        <ErrorBoundary>
          <QueryClientProvider client={queryClient}>
            <GrowthBookProvider visitorId={fingerprint}>
              <GrowthBookAttributesSetter />
              <CalendarPickerProvider>
                <AlertProvider
                  maxSnack={3}
                  anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
                  preventDuplicate
                >
                  <DialogContextController>
                    {fingerprint ? (
                      <SplitContextController
                        sdkKey={SPLIT_AUTH_KEY}
                        customerKey={fingerprint}
                      >
                        <Suspense fallback={<Loader size="large" />}>
                          {children}
                        </Suspense>
                      </SplitContextController>
                    ) : (
                      <Suspense fallback={<Loader size="large" />}>
                        {children}
                      </Suspense>
                    )}
                  </DialogContextController>
                </AlertProvider>
              </CalendarPickerProvider>
            </GrowthBookProvider>
          </QueryClientProvider>
        </ErrorBoundary>
      </CollegeHunksTheme>
    </StylesProvider>
  );
});
